import { Box, CircularProgress, Tab, Tabs } from "@mui/material";
import { useGetAllContractorsForRollingNumbersQuery } from "@repo/server-api";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";

import { BccRecipientLists } from "../components/BccRecipientLists";

type ContractorTab = {
	id: string;
	displayName: string;
};

export function MailConfiguration() {
	const { t } = useTranslation();

	const [selectedContractorId, setSelectedContractorId] = useState<string | null>(null);

	const { data: contractorsData, loading: contractorsLoading } =
		useGetAllContractorsForRollingNumbersQuery({
			fetchPolicy: "cache-and-network",
			onCompleted(data) {
				// Auto-select first contractor when data loads
				if (data?.allContacts?.nodes && data.allContacts.nodes.length > 0 && !selectedContractorId) {
					const sorted = [...data.allContacts.nodes].sort((a, b) =>
						a.displayName.localeCompare(b.displayName),
					);
					setSelectedContractorId(sorted[0].id);
				}
			},
		});

	const contractors: ContractorTab[] = useMemo(() => {
		if (contractorsData?.allContacts?.nodes) {
			return [...contractorsData.allContacts.nodes].sort((a, b) =>
				a.displayName.localeCompare(b.displayName),
			);
		}
		return [];
	}, [contractorsData]);

	const selectedContractor = useMemo(() => {
		return contractors.find((c) => c.id === selectedContractorId) ?? null;
	}, [contractors, selectedContractorId]);

	const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
		setSelectedContractorId(newValue);
	};

	if (contractorsLoading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
				<CircularProgress />
			</Box>
		);
	}

	if (contractors.length === 0) {
		return (
			<Box sx={{ height: "100%", width: "100%" }}>
				<h3 className="purpleHeader">{t("mail-configuration")}</h3>
				<Box sx={{ p: 2 }}>{t("no-contractors-available")}</Box>
			</Box>
		);
	}

	return (
		<Box sx={{ height: "100%", width: "100%" }}>
			<h3 className="purpleHeader">{t("mail-configuration")}</h3>

			<Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 2 }}>
				<Tabs
					value={selectedContractorId}
					onChange={handleTabChange}
					variant="scrollable"
					scrollButtons="auto"
					aria-label="mail configuration tabs"
				>
					{contractors.map((contractor) => (
						<Tab
							key={contractor.id}
							label={contractor.displayName}
							value={contractor.id}
							data-testid={`tab-contractor-${contractor.id}`}
						/>
					))}
				</Tabs>
			</Box>

			{selectedContractor && (
				<BccRecipientLists
					contact={{ id: selectedContractor.id, displayName: selectedContractor.displayName } as any}
					getContact={() => {}}
				/>
			)}
		</Box>
	);
}
